/* eslint-disable react/jsx-one-expression-per-line */
import { useCallback, useEffect, useState } from 'react';

import useTitle from '../../../hooks/UseTitle';
import { getForms } from '../../../services/inquiry';
import config from '../../../config';
import { generateSecurityId } from '../../../utils/base62';
import { convertWebDBPaginationToLaravel } from '../../../utils/pagination';
import Paginator from '../../../components/Paginator';
import { PaginationMetaLink } from '../../../components/Modals/Paginator';
import { getSsoToken } from '../../../services/api';

import { InquiryForm } from '../../../models/Inquiry/Inquiry';

import classes from '../inquiries.module.css';

function InquiryManagement() {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<InquiryForm[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({ from: 0, to: 0 });
  const [links, setLinks] = useState<PaginationMetaLink[]>([]);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const perPage = 10;

  const fetchData = useCallback(
    (page: number) => {
      setIsLoading(true);

      getForms(perPage, page)
        .then((res) => {
          setData(res?.data?.data || []);

          if (res?.data?.metadata) {
            const pagination = convertWebDBPaginationToLaravel(res.data.metadata);
            setTotal(pagination.total);
            setPaginationInfo({ from: pagination.from, to: pagination.to });
            setLinks(pagination.links);
            setCurrentPage(pagination.current_page);
          }
        })
        .catch((err) => {
          console.warn(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handlePageChange = (page: number) => {
    fetchData(page);
  };

  const handleSsoClick = async () => {
    try {
      setIsSsoLoading(true);

      // Get SSO token from backend
      const { token: ssoToken } = await getSsoToken();

      if (!ssoToken) {
        console.error('SSO token not received');
        alert('SSO認証トークンの取得に失敗しました。');
        return;
      }

      // Generate security_id
      const securityId = generateSecurityId();

      // Build SSO URL with parameters
      const ssoUrl = new URL(config.api.webdbSsoUrl);
      ssoUrl.searchParams.set('token', ssoToken);
      ssoUrl.searchParams.set('security_id', securityId);
      ssoUrl.searchParams.set('redirect', '/tables');

      // Open in new window
      window.open(ssoUrl.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error during SSO redirect:', error);
      alert('SSO認証に失敗しました。もう一度お試しください。');
    } finally {
      setIsSsoLoading(false);
    }
  };

  useTitle('ビルドセルフ');

  return (
    <main className={classes.main}>
      <div className="container-fluid p-3">
        <div className="mb-3">
          <h3 className="h3 d-inline align-middle">お問合せ管理</h3>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex align-items-center">
                <h4 className="h4 mt-2 mb-0">お問合せ一覧</h4>
                <div className="text-center flex-grow-1">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSsoClick}
                    disabled={isSsoLoading}
                  >
                    {isSsoLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        読み込み中...
                      </>
                    ) : (
                      'お問合せ管理画面へ'
                    )}
                  </button>
                </div>
              </div>
              <div className="card-body p-0">
                <table className="table table-striped table-hover table-bordered table-nowrap">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: '5%' }}>No</th>
                      <th>名前</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      data?.map((row) => (
                        <tr key={row.id}>
                          <td className="text-center">{row.id}</td>
                          <td>{row.name ?? ''}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
              <div className="card-footer bg-white pt-1 pb-3">
                {isLoading
                  ? (
                    <div className="spinner-border spinner-border-sm text-primary mt-2" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  )
                  : (
                    <div className="d-flex mt-2">
                      <div className="page-item-count">
                        <div style={{ fontSize: 14 }}>
                          {total === 0
                            ? '０件'
                            : `全 ${total} 件中 ${paginationInfo.from} 〜 ${paginationInfo.to} 件を表示`}
                        </div>
                      </div>
                      <Paginator
                        currentPage={currentPage}
                        changePage={handlePageChange}
                        links={links}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default InquiryManagement;
